/**
 * Table Service - Configuration and utilities for enhanced tables
 */

export interface TableConfig {
  id: string;
  name?: string;
  showToolbar?: boolean;
  showPagination?: boolean;
  pageSize?: number;
  sortable?: boolean;
  filterable?: boolean;
  exportable?: boolean;
  refreshable?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: string[];
  callbacks?: {
    onRefresh?: () => void;
    onExport?: () => void;
    onFilter?: (filters?: any) => void;
    onSort?: (sorter?: any) => void;
  };
  features?: TableFeatures;
}

export interface TableFeatures {
  search: boolean;
  filter: boolean;
  sort: boolean;
  pagination: boolean;
  export: boolean;
  refresh: boolean;
  toolbar: boolean;
  enhanced: boolean;
  zebraStriped: boolean;
  hoverEffects: boolean;
  stickyHeaders: boolean;
  rowSelection: boolean;
  bulkActions: boolean;
  columnResize: boolean;
  columnReorder: boolean;
  columnVisibility: boolean;
}

export interface TablePresetCallbacks {
  onRefresh?: () => void;
  onExport?: () => void;
}

export interface TablePresetRegistration {
  callbacks?: TablePresetCallbacks;
  config?: Partial<TableConfig>;
}

const defaultConfig: TableConfig = {
  id: 'default',
  showToolbar: true,
  showPagination: true,
  pageSize: 10,
  sortable: true,
  filterable: true,
  exportable: true,
  refreshable: true,
  defaultPageSize: 20,
  pageSizeOptions: ['10', '20', '50', '100'],
  callbacks: {},
  features: {
    search: true,
    filter: true,
    sort: true,
    pagination: true,
    export: true,
    refresh: true,
    toolbar: true,
    enhanced: true,
    zebraStriped: true,
    hoverEffects: true,
    stickyHeaders: true,
    rowSelection: false,
    bulkActions: false,
    columnResize: false,
    columnReorder: false,
    columnVisibility: false,
  },
};

const defaultFeatures: TableFeatures = {
  search: true,
  filter: true,
  sort: true,
  pagination: true,
  export: true,
  refresh: true,
  toolbar: true,
  enhanced: true,
  zebraStriped: true,
  hoverEffects: true,
  stickyHeaders: true,
  rowSelection: false,
  bulkActions: false,
  columnResize: false,
  columnReorder: false,
  columnVisibility: false,
};

// Table configuration store
const tableConfigs = new Map<string, TableConfig>();
const tablePresets = new Map<string, TablePresetRegistration>();

export function useTableService(tableId?: string) {
  const config = tableId ? tableConfigs.get(tableId) || defaultConfig : defaultConfig;

  const setConfig = (newConfig: Partial<TableConfig>) => {
    if (tableId) {
      tableConfigs.set(tableId, { ...config, ...newConfig });
    }
  };

  const registerTable = (tableConfig: TableConfig) => {
    tableConfigs.set(tableConfig.id, tableConfig);
  };

  return {
    config,
    setConfig,
    registerTable,
    features: defaultFeatures,
  };
}

export function useMergedConfig(
  baseConfig: TableConfig | undefined,
  override: Partial<TableConfig> | undefined
): TableConfig {
  return {
    ...defaultConfig,
    ...baseConfig,
    ...override,
  };
}

export function useFeatureEnabled(
  feature: keyof TableFeatures,
  config?: TableConfig
): boolean {
  if (!config) return defaultFeatures[feature];

  const featureFromConfig = config.features?.[feature];
  if (typeof featureFromConfig === 'boolean') {
    return featureFromConfig;
  }

  switch (feature) {
    case 'sort':
      return config.sortable ?? defaultFeatures.sort;
    case 'filter':
      return config.filterable ?? defaultFeatures.filter;
    case 'pagination':
      return config.showPagination ?? defaultFeatures.pagination;
    case 'export':
      return config.exportable ?? defaultFeatures.export;
    case 'refresh':
      return config.refreshable ?? defaultFeatures.refresh;
    case 'toolbar':
      return config.showToolbar ?? defaultFeatures.toolbar;
    default:
      return defaultFeatures[feature] ?? false;
  }
}

export const TableUtils = {
  registerWithPreset: (
    tableId: string,
    _displayName: string,
    _preset: string,
    options?: TablePresetRegistration
  ) => {
    if (options) {
      tablePresets.set(tableId, options);
    }
    if (options?.config) {
      tableConfigs.set(tableId, { ...defaultConfig, id: tableId, ...options.config });
    }
  },

  formatCurrency: (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  },

  formatPercent: (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
    }).format(value / 100);
  },

  formatNumber: (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  },

  formatDate: (value: string | Date) => {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  formatDateTime: (value: string | Date) => {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },
};

export default TableUtils;
